import type {
  PortalComparisonMode,
  PortalCoverage,
} from '../../types/portalReport.js';
import type { ComparisonMode } from '../../types/advertisingHome.js';
import { comparisonRange } from '../advertisingHome/comparisonRange.js';
import type { DateRange } from '../reportingDateRange.js';

// CLIENT PORTAL round 2, item 4 — the comparison WINDOW. PURE, and deliberately
// free of any presentation import so the SERVER can use it: resolving which
// window to compare against decides which data to fetch, so it has to run where
// the fetching happens. The percentage itself is computed on the client (see
// ./deltas.ts), which is where the staff dashboards' computeDelta lives.
//
// ⚠️⚠️ THE RANGES COME FROM THE AM ADVERTISING HOME'S OWN `comparisonRange`,
// VERBATIM — not re-derived, not "the same logic". A client and their account
// manager disagreeing about what "vs last month" means is a credibility problem
// this report cannot afford, and it is exactly the kind of disagreement nobody
// notices until two reports are put side by side. That function already carries
// the month-end clamping (Mar 31 -> Feb 28/29) and the calendar-exact year shift.

/**
 * The portal's comparison vocabulary IS the AM home's. Typed separately only so
 * a client payload does not carry a dashboard type; this is the one place the
 * two meet, and a test asserts they agree member-for-member.
 */
export function toAmComparisonMode(mode: PortalComparisonMode): ComparisonMode {
  return mode as ComparisonMode;
}

export const PORTAL_COMPARISON_MODES: ReadonlyArray<{
  value: PortalComparisonMode;
  label: string;
}> = [
  { value: 'previous', label: 'Previous period' },
  { value: 'lastMonth', label: 'Previous month' },
  { value: 'lastYear', label: 'Previous year' },
];

/**
 * How each comparison window is NAMED — once, for every sentence that mentions
 * one.
 *
 * ⚠️⚠️ ONE VOCABULARY, because two sentences on the CRM band name the same
 * window: the basis note ("Compared with …") and the refusal ("your CRM history
 * does not reach …"). Two lists would let one band claim one basis and refuse a
 * different one, which is not merely wrong but unarguable.
 *
 * ⚠️ Each phrase has to read correctly in BOTH frames — "Compared with the
 * previous period." and "…does not reach the previous period." — which is why
 * they are noun phrases rather than labels. `PORTAL_COMPARISON_MODES`' labels
 * are written for a dropdown and do not fit a sentence.
 */
export const PORTAL_COMPARISON_PHRASE: Record<PortalComparisonMode, string> = {
  previous: 'the previous period',
  lastMonth: 'the same period last month',
  lastYear: 'the same period last year',
};

/** Resolve the comparison window. ⚠️ Delegates; never re-derives. */
export function portalComparisonRange(
  range: DateRange,
  mode: PortalComparisonMode,
): DateRange {
  return comparisonRange(range, toAmComparisonMode(mode));
}

/**
 * ⚠️⚠️ IS THE COMPARISON PERIOD ACTUALLY MEASURED?
 *
 * Backfill is tiered, so a comparison window can sit entirely before an
 * account's coverage begins. Comparing against it would compute "0 leads last
 * year → −100%", which is the report's own no-data-is-not-zero failure wearing
 * a different hat, and it would tell a client their performance collapsed when
 * the truth is we were not measuring then.
 *
 * Returns the reason when there is no honest comparison to draw, else null.
 */
export function comparisonUnavailableReason(
  compare: DateRange,
  coverage: PortalCoverage,
): string | null {
  if (!coverage.firstDate) {
    return 'There is no earlier data to compare with yet.';
  }
  if (compare.to < coverage.firstDate) {
    return 'We have no data for the comparison period, so there is nothing to compare with.';
  }
  if (compare.from < coverage.firstDate) {
    // Partially covered: a comparison against half a window understates the
    // change in a way the reader cannot see, so it is refused rather than
    // shown with a caveat nobody reads.
    return 'The comparison period is only partly covered by our reporting, so a change would be misleading.';
  }
  return null;
}
